import React, { useState } from 'react';
import SectionHeader from '../components/ui/SectionHeader';
import Badge from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiMinus } from 'react-icons/fi';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-slate-800 last:border-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex justify-between items-center text-left hover:text-blue-400 transition-colors"
      >
        <span className="font-medium text-slate-200">{question}</span>
        <SafeIcon icon={isOpen ? FiMinus : FiPlus} className="text-slate-500" />
      </button>
      {isOpen && (
        <div className="pb-4 text-sm text-slate-400 leading-relaxed whitespace-pre-line animate-fadeIn">
          {answer}
        </div>
      )}
    </div>
  );
};

export default function FAQ() {
  const categories = [
    {
      title: "Core Product & Value",
      questions: [
        { q: "What exactly is The Pitch Feed?", a: "The Pitch Feed is a football content solution built for sports platforms, publishers, and media businesses that need a steady flow of football coverage without having to build everything in-house. It helps customers publish more consistently, more efficiently, and at greater scale." },
        { q: "What are you actually selling?", a: "At the core, we sell access to football content and related products that help digital sports businesses grow their coverage, improve engagement, and expand their offering. Depending on the customer, that can include news content (40+ stories/day), widgets, and other supporting products." },
        { q: "What exactly are customers buying?", a: "Customers are buying access to a football news feed that delivers 40+ news stories per day." },
        { q: "How should I explain this to a non-technical buyer?", a: "The simplest way to explain it is: we help you publish more football content, more efficiently, without needing to build the whole setup yourself." },
        { q: "Why would a customer buy this instead of doing it in-house?", a: "Because building and maintaining in-house football coverage is expensive, time-consuming, and hard to scale. We help customers get consistent football content faster and with less operational burden." }
      ]
    },
    {
      title: "Sourcing & Editorial Process",
      questions: [
        { q: "Where do the news stories come from?", a: "We buy news from external sources and add our own editorial input. We also write fully original stories ourselves." },
        { q: "Do you produce your own content, or only repurpose news?", a: "We do both. We combine acquired news material with our own editorial additions, and we also create fully original stories ourselves. That gives customers a broader and more usable football content offering." },
        { q: "Is the content edited, or just raw feed material?", a: "It is not just raw feed material. We add editorial value, create our own additions, and also produce original stories ourselves." },
        { q: "Will the content feel generic?", a: "The goal is to provide content that is practical, relevant, and useful. Because there is editorial work involved, the value is not simply in volume but in making the content more usable for publishing." }
      ]
    },
    {
      title: "Trial & Commercial Policy",
      questions: [
        { q: "Do you offer a free trial?", a: "No, we do not offer a free trial." },
        { q: "Why don’t you offer a free trial?", a: "Because the product has significant underlying costs, including both technical costs and content costs. For that reason, a free trial is not commercially viable for us." },
        { q: "What if the buyer says it is too expensive?", a: "A good answer is that the comparison should not be against doing nothing, but against the cost of creating and maintaining equivalent football coverage internally. We create more output and value than we cost." },
        { q: "How can we evaluate the content if there is no free trial?", a: "The best reference point is our public news page, where buyers can follow the news and get a sense of the type of content we produce: https://thepitchfeed.com/news" }
      ]
    },
    {
      title: "Usage & Business Fit",
      questions: [
        { q: "Is this only for large media companies?", a: "No. It can also be a strong fit for smaller and mid-sized businesses that want to increase their football output without hiring a full internal editorial team." },
        { q: "Is this a replacement for an internal team?", a: "Not necessarily. For some customers, it can reduce pressure on the internal team. For others, it works best as a way to complement existing editorial resources and increase output." },
        { q: "Can we start with one language and expand later?", a: "Yes, that is often a sensible commercial path. Many buyers prefer to start with one market, prove the value, and then grow from there." }
      ]
    }
  ];

  return (
    <section>
      <SectionHeader id="faq" title="17-20. Comprehensive FAQ Bank" subtitle="50+ buyer questions and approved sales answers." />
      
      <div className="space-y-8">
        {categories.map((cat, i) => (
          <div key={i} className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
            <div className="bg-slate-800 px-6 py-3 border-b border-slate-700">
              <h3 className="font-bold text-white text-sm uppercase tracking-wider">{cat.title}</h3>
            </div>
            <div className="px-6">
              {cat.questions.map((item, j) => (
                <FAQItem key={j} question={item.q} answer={item.a} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}